import { SkeletonElement } from '@notino/react-styleguide'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

// types
import { SalonEmployeesResponse } from '../../../../types/types'

// utils
import { AppContext } from '../../../../utils/appProvider'
import { SALON_EMPLOYEES_LIMIT } from '../../../../utils/enums'

// hooks
import useMessages from '../../../../hooks/useMessages'

// components
import ErrorScreen from '../../../../atoms/ErrorScreen/ErrorScreen'
import Empty from '../../../../atoms/Empty/Empty'
import VisuallyHidden from '../../../../atoms/VisuallyHidden/VisuallyHidden'
import ElementsSlider from '../../../../components/ElementsSlider/ElementsSlider'

// styles
import * as SC from './SalonEmployeesStyles'

type SalonEmployeesProps = {
	salonID: string
	employeesData: SalonEmployeesResponse | undefined
}

const LOADING_SKELETONS_COUNT = 5

const SalonEmployees = (props: SalonEmployeesProps) => {
	const { salonID, employeesData } = props
	const { apiBrowser } = useContext(AppContext)
	const { messages } = useMessages()
	const intl = useIntl()

	const [employees, setEmployees] = useState<SalonEmployeesResponse | undefined>(employeesData)
	const [isLoading, setIsLoading] = useState(false)
	const [isLoadingMore, setIsLoadingMore] = useState(false)
	const [statusMessage, setStatusMessage] = useState('')

	const elementsSliderRef = useRef<{ scrollElementRef: HTMLDivElement | null }>(null)
	const skeletonRef = useRef<HTMLLIElement>(null)

	const fetchEmployees = useCallback(
		async (data: { page: number }) => {
			const isLoadMore = data.page > 1

			if (isLoadMore) {
				setIsLoadingMore(true)
				setStatusMessage(messages['Loading more team members'])
			} else {
				setIsLoading(true)
				setStatusMessage(messages['Loading team'])
			}

			try {
				const response = await apiBrowser.b2c.getSalonEmployees(salonID, { page: data.page, limit: SALON_EMPLOYEES_LIMIT })
				const loadedCount = response.employees.length

				setEmployees((prev) => ({
					employees: [...(prev?.employees ?? []), ...response.employees],
					pagination: response.pagination
				}))

				if (isLoadMore) {
					setStatusMessage(
						intl.formatMessage(
							{
								id: '{count, plural, one {# more team member was loaded} other {# more team members were loaded}}',
								defaultMessage: '{count, plural, one {# more team member was loaded} other {# more team members were loaded}}'
							},
							{ count: loadedCount }
						)
					)
				} else {
					setStatusMessage('')
				}
			} catch {
				setEmployees(undefined)
				setStatusMessage(messages['Something went wrong. Data was not loaded.'])
			} finally {
				if (isLoadMore) {
					setIsLoadingMore(false)
				} else {
					setIsLoading(false)
				}
			}
		},
		[apiBrowser.b2c, salonID, messages, intl]
	)

	// Intersection Observer for lazy loading
	useEffect(() => {
		if (!elementsSliderRef.current?.scrollElementRef || !skeletonRef.current || isLoadingMore || isLoading) {
			return undefined
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					// Load next page if available
					if (employees && employees.pagination.page < employees.pagination.totalPages && !isLoadingMore && !isLoading) {
						fetchEmployees({ page: employees.pagination.page + 1 })
					}
				}
			},
			{
				root: elementsSliderRef.current.scrollElementRef,
				threshold: 0.5 // Trigger when 50% visible
			}
		)

		observer.observe(skeletonRef.current)

		return () => {
			observer.disconnect()
		}
	}, [employees?.pagination.page, employees?.pagination.totalPages, isLoadingMore, employees, isLoading, fetchEmployees])

	const getContent = () => {
		if (isLoading) {
			return (
				<ElementsSlider ref={elementsSliderRef} asList>
					{Array.from({ length: LOADING_SKELETONS_COUNT }).map((_, index) => (
						<SC.EmployeeContainer key={index} aria-hidden={true}>
							<SC.SkeletonAvatarElement width={'80px'} height={'80px'} />
							<SkeletonElement width={'100px'} height={'1.25rem'} />
						</SC.EmployeeContainer>
					))}
				</ElementsSlider>
			)
		}

		if (!employees) {
			return <ErrorScreen icon={null} minHeight={'164px'} button={{ onClick: () => fetchEmployees({ page: 1 }) }} />
		}

		if (employees?.employees.length === 0) {
			return <Empty icon={null} minHeight={'164px'} />
		}

		return (
			<ElementsSlider ref={elementsSliderRef} asList>
				{employees.employees.map((employee) => (
					<SC.EmployeeContainer key={employee.id}>
						<SC.EmployeeImageContainer>
							<img alt={''} loading={'lazy'} src={employee.image.resizedImages.thumbnail} width={80} height={80} />
						</SC.EmployeeImageContainer>
						<SC.EmployeeName>{employee.firstName}</SC.EmployeeName>
					</SC.EmployeeContainer>
				))}
				{employees.pagination.page < employees.pagination.totalPages && (
					<SC.EmployeeContainer ref={skeletonRef} aria-hidden={true}>
						<SC.SkeletonAvatarElement width={'80px'} height={'80px'} />
						<SkeletonElement width={'100px'} height={'1.25rem'} />
					</SC.EmployeeContainer>
				)}
			</ElementsSlider>
		)
	}

	return (
		<SC.EmployeesContainer>
			<VisuallyHidden as='div' role='status' aria-live='polite' aria-atomic='true'>
				{statusMessage}
			</VisuallyHidden>
			{getContent()}
		</SC.EmployeesContainer>
	)
}

export default SalonEmployees
